import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface GoogleCalendarPrefs {
  calendar_id: string | null;
  sync_ots: boolean;
  sync_ausencias: boolean;
  last_sync_at: string | null;
}

export interface GoogleCalendarStatus {
  connected: boolean;
  email?: string | null;
  prefs?: GoogleCalendarPrefs;
  calendarios?: { id: string; summary: string }[];
}

async function invokeError(error: unknown): Promise<string> {
  const anyErr = error as { context?: { text?: () => Promise<string> }; message?: string };
  if (anyErr?.context?.text) {
    try {
      const text = await anyErr.context.text();
      try {
        const json = JSON.parse(text);
        return json.error || text;
      } catch {
        return text;
      }
    } catch { /* ignore */ }
  }
  return anyErr?.message || 'Error desconocido';
}

export function useGoogleCalendar() {
  const { toast } = useToast();
  const [status, setStatus] = useState<GoogleCalendarStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [connecting, setConnecting] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('google-calendar-status');
      if (error) throw error;
      setStatus(data as GoogleCalendarStatus);
    } catch (error) {
      console.error('google-calendar-status:', await invokeError(error));
      setStatus({ connected: false });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  const sync = useCallback(async (silent = false) => {
    setSyncing(true);
    try {
      const { data, error } = await supabase.functions.invoke('google-calendar-sync', {
        body: { origin: window.location.origin },
      });
      if (error) throw error;
      if (!silent) {
        const d = data as { creados?: number; actualizados?: number; borrados?: number };
        toast({
          title: 'Sincronización completada',
          description: `${d.creados ?? 0} creados · ${d.actualizados ?? 0} actualizados · ${d.borrados ?? 0} eliminados`,
        });
      }
      await refresh();
    } catch (error) {
      const message = await invokeError(error);
      if (!silent) toast({ title: 'Error al sincronizar', description: message, variant: 'destructive' });
      console.error('google-calendar-sync:', message);
    } finally {
      setSyncing(false);
    }
  }, [refresh, toast]);

  const connect = useCallback(async () => {
    setConnecting(true);
    const popup = window.open('', 'raven-google-oauth', 'width=520,height=680');
    if (!popup) {
      setConnecting(false);
      toast({ title: 'Ventana bloqueada', description: 'Permite las ventanas emergentes e inténtalo de nuevo.', variant: 'destructive' });
      return;
    }
    try {
      const { data, error } = await supabase.functions.invoke('google-oauth-start', {
        body: { origin: window.location.origin },
      });
      if (error) throw error;

      const completion = new Promise<void>((resolve, reject) => {
        let poll: number | undefined;
        let settled = false;
        const cleanup = () => {
          window.removeEventListener('message', onMessage);
          if (poll !== undefined) window.clearInterval(poll);
        };
        const onMessage = (event: MessageEvent) => {
          if (event.origin !== window.location.origin) return;
          const type = event.data?.type;
          if (type !== 'googleCalendarConnected' && type !== 'googleCalendarFailed') return;
          if (settled) return;
          settled = true;
          cleanup();
          if (type === 'googleCalendarConnected') resolve();
          else reject(new Error(event.data?.message || 'La conexión con Google no se completó.'));
        };
        window.addEventListener('message', onMessage);
        poll = window.setInterval(() => {
          if (!popup.closed) return;
          cleanup();
          if (!settled) {
            settled = true;
            reject(new Error('Ventana cerrada antes de completar la conexión.'));
          }
        }, 600);
      });

      popup.location.href = (data as { authorizationUrl: string }).authorizationUrl;
      await completion;
      toast({ title: 'Google Calendar conectado' });
      await sync(true);
      await refresh();
    } catch (error) {
      popup.close();
      const message = await invokeError(error);
      toast({ title: 'No se pudo conectar', description: message, variant: 'destructive' });
    } finally {
      setConnecting(false);
    }
  }, [refresh, sync, toast]);

  const disconnect = useCallback(async (borrarEventos: boolean) => {
    try {
      const { error } = await supabase.functions.invoke('google-calendar-disconnect', {
        body: { borrarEventos },
      });
      if (error) throw error;
      toast({ title: 'Google Calendar desconectado' });
      await refresh();
    } catch (error) {
      toast({ title: 'Error al desconectar', description: await invokeError(error), variant: 'destructive' });
    }
  }, [refresh, toast]);

  const updatePrefs = useCallback(async (patch: Partial<GoogleCalendarPrefs>) => {
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData.user?.id;
    if (!userId) return;
    const { error } = await supabase
      .from('google_calendar_prefs')
      .upsert({ user_id: userId, ...patch }, { onConflict: 'user_id' });
    if (error) {
      toast({ title: 'No se pudieron guardar las preferencias', description: error.message, variant: 'destructive' });
      return;
    }
    setStatus(prev => prev?.prefs ? { ...prev, prefs: { ...prev.prefs, ...patch } as GoogleCalendarPrefs } : prev);
    await sync(true);
  }, [sync, toast]);

  return { status, loading, syncing, connecting, connect, disconnect, sync, updatePrefs, refresh };
}
